import React, { useState, useEffect } from 'react';
import { Activity, Eye, EyeOff, Users, Plus, UserCheck, ChevronUp } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import Modal from './Modal';
import { useModal } from '../hooks/useModal';
import { meetupApi } from '../services/meetupApi';
import {
  resetApiTracking,
  __subscribeApiCalls,
  __getApiCallsSnapshot,
  type ApiCall,
} from '../services/apiTrackerState';

interface ApiTrackerProps {
  isVisible?: boolean;
  onToggleVisibility?: () => void;
}

export const ApiTracker: React.FC<ApiTrackerProps> = ({ 
  isVisible = false, 
  onToggleVisibility 
}) => {
  const [apiCalls, setApiCalls] = useState<{ [key: string]: ApiCall }>({});
  const [isMinimized, setIsMinimized] = useState(false);
  const [isFillingMeetup, setIsFillingMeetup] = useState(false);
  const [meetupData, setMeetupData] = useState<{ id: string; currentParticipants: number; maxParticipants: number } | null>(null);
  const location = useLocation();
  const { modalState, hideModal, showSuccess, showWarning, showError } = useModal();

  useEffect(() => {
    // Register this component for updates
    const unsubscribe = __subscribeApiCalls((calls) => {
      setApiCalls(calls);
    });

    // Set initial state
    setApiCalls(__getApiCallsSnapshot());

    // Cleanup on unmount
    return unsubscribe;
  }, []);

  // Fetch meetup data when on a meetup page
  useEffect(() => {
    const fetchMeetupData = async () => {
      // Check if we're on a meetup details page
      const match = location.pathname.match(/^\/meetups\/(\d+)/);
      if (match) {
        const meetupId = match[1];
        try {
          const meetup = await meetupApi.getMeetup(meetupId);
          const meetupData = (meetup as any).data || meetup;
          setMeetupData({
            id: meetupId,
            currentParticipants: meetupData.participant_count || 0,
            maxParticipants: meetupData.max_participants || 10
          });
        } catch (error) {
          console.error('Failed to fetch meetup data:', error);
          setMeetupData(null);
        }
      } else {
        setMeetupData(null);
      }
    };

    fetchMeetupData();
  }, [location.pathname]);

  const sortedApiCalls = Object.values(apiCalls).sort((a, b) => (b.count + b.cacheHits) - (a.count + a.cacheHits));
  const totalCalls = sortedApiCalls.reduce((sum, call) => sum + call.count, 0);
  const totalCacheHits = sortedApiCalls.reduce((sum, call) => sum + call.cacheHits, 0);

  const getApiDisplayName = (api: string): string => {
    switch (api) {
      case 'places_search':
        return 'Places Search';
      case 'place_details':
        return 'Place Details';
      case 'place_photo':
        return 'Place Photos';
      case 'distance_matrix':
        return 'Distance Matrix';
      case 'geocoding':
        return 'Geocoding';
      case 'reverse_geocoding':
        return 'Reverse Geocoding';
      case 'maps_javascript':
        return 'Maps JavaScript';
      default:
        return api.split('_').map(word => 
          word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
    }
  };

  const getApiColor = (api: string): string => {
    switch (api) {
      case 'places_search':
        return 'bg-gray-500';
      case 'place_details':
        return 'bg-green-500';
      case 'place_photo':
        return 'bg-purple-500';
      case 'distance_matrix':
        return 'bg-orange-500';
      case 'geocoding':
        return 'bg-teal-500';
      case 'reverse_geocoding':
        return 'bg-secondary-500';
      case 'maps_javascript':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const formatTime = (date: Date): string => {
    return date.toLocaleTimeString('en-US', { 
      hour12: false, 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit' 
    });
  };

  const fillMeetup = async () => {
    if (!meetupData) return;
    
    try {
      setIsFillingMeetup(true);
      const response = await meetupApi.fillParticipants(meetupData.id);
      console.log('Fill meetup response:', response);
      
      // Response structure: response already contains the data
      const addedCount = response.addedParticipants || response.data?.addedParticipants || 0;
      
      // Refresh meetup data after filling
      const updatedMeetup = await meetupApi.getMeetup(meetupData.id);
      const updatedData = (updatedMeetup as any).data || updatedMeetup;
      setMeetupData({
        id: meetupData.id,
        currentParticipants: updatedData.participant_count || 0,
        maxParticipants: updatedData.max_participants || 10
      });
      
      if (addedCount === 0) {
        showWarning('Meetup Full', `Meetup ${meetupData.id} is already at full capacity!`);
      } else {
        showSuccess('Participants Added', `Successfully added ${addedCount} participants to meetup ${meetupData.id}!`);
      }
    } catch (error: any) {
      console.error('Failed to fill meetup:', error);
      showError('Fill Meetup Failed', `Failed to fill meetup: ${error.response?.data?.error || error.message}`);
    } finally {
      setIsFillingMeetup(false);
    }
  };

  return (
    <div 
      className={`fixed top-16 left-0 right-0 z-40 bg-white shadow-lg border-b border-gray-200 transition-all duration-300 transform ${
        isVisible ? 'translate-y-0' : '-translate-y-full'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between py-3 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <Activity className="w-5 h-5 text-secondary-700" />
            <h3 className="text-sm font-semibold text-gray-900">
              API Monitor
            </h3>
            <div className="flex items-center gap-2">
              <span className="bg-red-100 text-red-800 text-xs font-medium px-2 py-1 rounded-full" title="Actual API calls">
                {totalCalls} API
              </span>
              <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full" title="Cache hits">
                {totalCacheHits} Cache
              </span>
              {totalCalls + totalCacheHits > 0 && (
                <span className="bg-gray-100 text-secondary-900 text-xs font-medium px-2 py-1 rounded-full" title="Cache efficiency">
                  {Math.round((totalCacheHits / (totalCalls + totalCacheHits)) * 100)}% Hit Rate
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsMinimized(!isMinimized)}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              title={isMinimized ? 'Expand' : 'Collapse'}
            >
              {isMinimized ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            </button>
            <button
              onClick={() => resetApiTracking()}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors text-gray-600"
              title="Reset counters"
            >
              Reset
            </button>
            {onToggleVisibility && (
              <button
                onClick={onToggleVisibility}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors text-gray-600"
                title="Close API Monitor"
              >
                <ChevronUp className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        {!isMinimized && (
          <div className="py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* API Calls List */}
              <div className="lg:col-span-2">
                {sortedApiCalls.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <Activity className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No API calls yet</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Interact with the page to see API usage
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto pr-2">
                    {sortedApiCalls.map((call) => (
                      <div
                        key={call.api}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div
                            className={`w-3 h-3 rounded-full ${getApiColor(call.api)}`}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900">
                              {getApiDisplayName(call.api)}
                            </p>
                            {call.details && (
                              <p className="text-xs text-gray-500 truncate" title={call.details}>
                                {call.details}
                              </p>
                            )}
                            <p className="text-xs text-gray-400">
                              Last: {formatTime(call.lastCalled)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {call.count > 0 && (
                            <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full" title="API calls">
                              {call.count}
                            </span>
                          )}
                          {call.cacheHits > 0 && (
                            <span className="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full" title="Cache hits">
                              {call.cacheHits}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Summary and Debug Controls */}
              <div className="space-y-4">
                {/* Summary */}
                {sortedApiCalls.length > 0 && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Summary</h4>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Actual API calls:</span>
                        <span className="font-semibold text-red-600">{totalCalls}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Cache hits:</span>
                        <span className="font-semibold text-green-600">{totalCacheHits}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Total requests:</span>
                        <span className="font-semibold text-gray-900">{totalCalls + totalCacheHits}</span>
                      </div>
                      <div className="pt-2 border-t border-gray-200">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">Cache efficiency:</span>
                          <span className="font-semibold text-secondary-700">
                            {totalCalls + totalCacheHits > 0 
                              ? `${Math.round((totalCacheHits / (totalCalls + totalCacheHits)) * 100)}%`
                              : '0%'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Debug Controls */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Debug Controls</h4>
                  {meetupData ? (
                    <>
                      <div className="text-xs text-gray-600 mb-3">
                        Meetup #{meetupData.id}: {meetupData.currentParticipants}/{meetupData.maxParticipants} participants
                      </div>
                      <button
                        onClick={fillMeetup}
                        disabled={isFillingMeetup || meetupData.currentParticipants >= meetupData.maxParticipants}
                        className="w-full bg-violet-600 hover:bg-violet-700 disabled:bg-gray-400 text-white text-xs font-medium px-3 py-2 rounded-md flex items-center justify-center gap-2 transition-colors"
                        title={meetupData.currentParticipants >= meetupData.maxParticipants ? 'Meetup is already full' : `Add ${meetupData.maxParticipants - meetupData.currentParticipants} participants`}
                      >
                        {isFillingMeetup ? (
                          <>
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                            Adding...
                          </>
                        ) : meetupData.currentParticipants >= meetupData.maxParticipants ? (
                          <>
                            <UserCheck className="w-3 h-3" />
                            Meetup Full
                          </>
                        ) : (
                          <>
                            <Users className="w-3 h-3" />
                            <Plus className="w-3 h-3" />
                            Fill to Capacity ({meetupData.maxParticipants - meetupData.currentParticipants} needed)
                          </>
                        )}
                      </button>
                    </>
                  ) : (
                    <div className="text-xs text-gray-500 italic">
                      Navigate to a meetup page to enable fill button
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Minimized view */}
        {isMinimized && (totalCalls > 0 || totalCacheHits > 0) && (
          <div className="py-2">
            <div className="flex justify-center gap-8">
              <div className="text-center">
                <div className="text-lg font-bold text-red-600">{totalCalls}</div>
                <div className="text-xs text-gray-600">API Calls</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-green-600">{totalCacheHits}</div>
                <div className="text-xs text-gray-600">Cache Hits</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-secondary-700">
                  {Math.round((totalCacheHits / (totalCalls + totalCacheHits)) * 100)}%
                </div>
                <div className="text-xs text-gray-600">Hit Rate</div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Modal for alerts */}
      <Modal
        isOpen={modalState.isOpen}
        onClose={hideModal}
        title={modalState.title}
        message={modalState.message}
        type={modalState.type}
        confirmText={modalState.confirmText}
        cancelText={modalState.cancelText}
        onConfirm={modalState.onConfirm}
        showCancel={modalState.showCancel}
      />
    </div>
  );
};

export default ApiTracker;